import {
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalParagraph,
    ModalTitle,
    ModalCloseButton
} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {Spinner} from '@components/ui/spinner'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {format} from 'date-fns'
import {Checkbox} from '@components/commons/checkbox'
import {z} from 'zod'
import {Controller, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {DatePicker} from '@components/commons/date-picker'
import {TimePicker} from '@components/commons/time-picker'
import {useAssignmentCoordination} from '@features/assignment/queries/useAssignmentCoordination'
import {ServiceType} from '@features/assignment/types.ts'
import {useEffect} from 'react'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'

const ValidationSchema = z
    .object({
        date: z.date().optional(),
        time: z.string().optional(),
        unknown_flag: z.boolean()
    })
    .superRefine((values, context) => {
        if (!values.unknown_flag && !values.date) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'The date field is required',
                path: ['date']
            })
        }
        if (!values.unknown_flag && !values.time) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'The time field is required',
                path: ['time']
            })
        }
    })
type ValidationSchema = z.infer<typeof ValidationSchema>

export const FreelancerNotAvailableModal = ({
    assignmentId,
    taskId,
    onClose,
    serviceType
}: {
    assignmentId: number
    taskId: number
    onClose: () => void
    serviceType: ServiceType
}) => {
    const {t} = useTranslation()
    const freelancerNotAvailableMutation = useAssignmentCoordination({
        assignmentId,
        taskId,
        options: {onSuccess: onClose}
    })
    const form = useForm<ValidationSchema>({
        resolver: zodResolver(ValidationSchema),
        defaultValues: {
            unknown_flag: false
        }
    })
    const isUnknownFlagChecked = useWatch({name: 'unknown_flag', control: form.control})
    const handleSubmit = form.handleSubmit(values => {
        if (values.unknown_flag) {
            freelancerNotAvailableMutation.mutate({status: 'photographer_not_available'})
        } else {
            // the schema's superRefine guarantees both are set on this branch, but TS can't see it
            if (!values.date || !values.time) {
                return
            }

            //date to send adapted to safari format too
            const dateToSend = format(
                new Date(`${format(values.date, 'yyyy-MM-dd')} ${values.time}`.replace(/-/g, '/')),
                'yyyy-MM-dd HH:mm:ss'
            )

            freelancerNotAvailableMutation.mutate({
                status: 'photographer_not_available',
                date: dateToSend
            })
        }
    })

    useEffect(() => {
        if (isUnknownFlagChecked) {
            form.resetField('date', undefined)
            form.resetField('time', undefined)
        }
    }, [isUnknownFlagChecked])

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-120" showCloseButton={false}>
                <ModalHeader>
                    <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-2">
                        <ModalTitle>{t('assignment:descriptions:freelancer_not_available_modal:title')}</ModalTitle>
                        <ModalParagraph>
                            {t(
                                `assignment:descriptions:freelancer_not_available_modal:subtitle:${serviceType}` as TKey
                            )}
                        </ModalParagraph>
                    </div>
                    <div className="flex gap-4">
                        <Controller
                            name="date"
                            control={form.control}
                            render={({field}) => (
                                <DatePicker
                                    asModal
                                    triggerProps={{label: t('commons:date'), className: 'w-full'}}
                                    disabled={isUnknownFlagChecked}
                                    minDate={new Date()}
                                    formatter={date => format(date, 'yyyy-MM-dd')}
                                    mode="single"
                                    numMonths={1}
                                    selectedDates={field.value ? [field.value] : []}
                                    onDatesChange={dates => field.onChange(dates[0])}
                                />
                            )}
                        />
                        <Controller
                            name="time"
                            control={form.control}
                            render={({field}) => (
                                <TimePicker
                                    asModal
                                    triggerProps={{label: t('commons:time'), className: 'w-full'}}
                                    disabled={isUnknownFlagChecked}
                                    selectedTime={field.value ?? ''}
                                    onSelectTime={time => field.onChange(time)}
                                />
                            )}
                        />
                    </div>
                    <Controller
                        name="unknown_flag"
                        control={form.control}
                        render={({field}) => (
                            <Checkbox
                                id="unknown_flag"
                                label={t('assignment:descriptions:freelancer_not_available_modal:checkbox_label')}
                                onCheckedChange={checked => field.onChange(checked)}
                            />
                        )}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="tertiary" disabled={freelancerNotAvailableMutation.isPending}>
                        {t('commons:close')}
                    </Button>
                    <Button
                        disabled={freelancerNotAvailableMutation.isPending || !form.formState.isValid}
                        onClick={handleSubmit}
                    >
                        {t('commons:confirm')}
                        {freelancerNotAvailableMutation.isPending && <Spinner size={20} />}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
